import EntityFilter from "src/components/common/EntityFilter";
import { Box, Chip, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import SearchBar from "src/components/common/SearchField";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { path } from "src/constants/path";
import { dateAndTimeStamp } from "src/utils/helper";
import { getFileByKey } from "src/utils/api/fileupload";
import { getPayments } from "src/utils/api/payment";
import { useAppSelector } from "src/redux/store";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "Demand Note Pending",
  PAYMENT_PENDING: "Payment Pending",
  PAYMENT_VERIFICATION: "Payment Verification",
  PAYMENT_REJECTED: "Payment Rejected",
  PAYMENT_RECEIVED: "Payment Received",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "#757575",
  PAYMENT_PENDING: "#1976d2",
  PAYMENT_VERIFICATION: "#ed6c02",
  PAYMENT_REJECTED: "#d32f2f",
  PAYMENT_RECEIVED: "#2e7d32",
};

const paymentTabs = [
  { label: "All", value: "ALL" },
  { label: "Demand Note Pending", value: "DEMAND_NOTE_PENDING" },
  { label: "Payment Pending", value: "PAYMENT_PENDING" },
  { label: "Payment Verification", value: "PAYMENT_VERIFICATION" },
  { label: "Payment Rejected", value: "PAYMENT_REJECTED" },
  { label: "Payment Received", value: "PAYMENT_RECEIVED" },
];

const formatAmount = (value?: number) =>
  value ? `₹ ${Number(value).toLocaleString("en-IN")}` : "—";

const DocLink = ({ docKey, label }: { docKey?: string; label: string }) => {
  const [loading, setLoading] = useState(false);
  if (!docKey) return <Box sx={{ color: "#999" }}>—</Box>;
  return (
    <Box
      component="span"
      onClick={async () => {
        if (loading) return;
        setLoading(true);
        try {
          const res = await getFileByKey(docKey);
          const url =
            (res as any)?.data?.data?.fileUrl || (res as any)?.data?.fileUrl;
          if (url) window.open(url, "_blank");
        } finally {
          setLoading(false);
        }
      }}
      sx={{ color: "#36B1A0", fontWeight: 500, cursor: "pointer" }}
    >
      {loading ? "Opening..." : label}
    </Box>
  );
};

const ACTION_LABEL_BY_STATUS: Record<string, string> = {
  PAYMENT_PENDING: "Upload Receipt",
  PAYMENT_REJECTED: "Re-upload Receipt",
  PAYMENT_RECEIVED: "View Details",
};

const Payment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Only entity users (userType === "ORGANIZATION") can upload/re-upload the
  // receipt. For other portal users (IO: userType === null, SNO:
  // userType === "INDIVIDUAL") the action is view-only.
  const userType = useAppSelector((state) => state.root.user.data.userType);
  const isEntity = userType === "ORGANIZATION";
  const [activeTab, setActiveTab] = useState(
    searchParams.get("currentTab") ?? "ALL"
  );
  const [searchText, setSearchText] = useState("");
  const [entityIds, setEntityIds] = useState<string[]>([]);
  const [paymentData, setPaymentData] = useState<{
    rows: any[];
    count: number;
  }>({ rows: [], count: 0 });

  const { isFetching } = useQuery(
    ["payments", activeTab, searchText, entityIds],
    () =>
      getPayments({
        status: activeTab,
        searchText: searchText || undefined,
        entityIds: entityIds.length ? entityIds.join(",") : undefined,
        offset: 0,
        limit: 200,
      }),
    {
      onSuccess: (res) => {
        const list = res?.data?.data ?? [];
        setPaymentData({
          count: res?.data?.total ?? list.length,
          rows: list.map((item: any, idx: number) => ({
            ...item,
            sno: idx + 1,
            entityName:
              [item?.entity?.firstName, item?.entity?.lastName]
                .filter(Boolean)
                .join(" ") || "—",
            projectName: item?.projectDetail?.name ?? "—",
            registrationNo: item?.projectDetail?.registrationNo ?? "—",
            amountLabel: formatAmount(item?.demandNoteAmount),
            createdAtLabel: dateAndTimeStamp(item?.created_at, "IST"),
          })),
        });
      },
      refetchOnMount: true,
    }
  );

  const renderActionLink = (params: any) => {
    const status = params.row?.status;
    // Non-entity users can't upload, so surface a view-only label instead of
    // "Upload Receipt" / "Re-upload Receipt".
    const label =
      !isEntity &&
      (status === "PAYMENT_PENDING" || status === "PAYMENT_REJECTED")
        ? "View Details"
        : ACTION_LABEL_BY_STATUS[status];
    if (!label) {
      if (status === "DEMAND_NOTE_PENDING")
        return <Box sx={{ color: "#999" }}>Awaiting demand note</Box>;
      if (status === "PAYMENT_VERIFICATION")
        return <Box sx={{ color: "#ed6c02" }}>Under verification</Box>;
      return <Box sx={{ color: "#999" }}>—</Box>;
    }
    return (
      <Box
        component="span"
        onClick={() =>
          navigate(
            `${path.paymentManagement}?${createSearchParams({
              epid: params.row.landOwnershipEpid,
            })}`
          )
        }
        sx={{
          color: "#36B1A0",
          fontWeight: 600,
          cursor: "pointer",
          textDecoration: "underline",
        }}
      >
        {label}
      </Box>
    );
  };

  const columns = [
    { field: "sno", headerName: "S.No", width: 80, sortable: false },
    {
      field: "landOwnershipEpid",
      headerName: "EPID",
      width: 200,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 220,
      sortable: false,
    },
    {
      field: "projectName",
      headerName: "Project Name",
      width: 200,
      sortable: false,
    },
    {
      field: "registrationNo",
      headerName: "Reg. No",
      width: 120,
      sortable: false,
    },
    {
      field: "actions",
      headerName: "Action",
      width: 220,
      sortable: false,
      renderCell: renderActionLink,
    },
    {
      field: "amountLabel",
      headerName: "Demand Note Amount",
      width: 180,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      width: 190,
      sortable: false,
      renderCell: (params: any) => {
        const s = params.row?.status;
        return (
          <Chip
            label={PAYMENT_STATUS_LABELS[s] ?? s}
            size="small"
            sx={{
              backgroundColor: `${PAYMENT_STATUS_COLORS[s] ?? "#757575"}20`,
              color: PAYMENT_STATUS_COLORS[s] ?? "#757575",
              fontWeight: 600,
              fontSize: "12px",
            }}
          />
        );
      },
    },
    {
      field: "demandNoteDoc",
      headerName: "Demand Note",
      width: 150,
      sortable: false,
      renderCell: (params: any) => (
        <DocLink docKey={params.row?.demandNoteDocumentKey} label="Download" />
      ),
    },
    {
      field: "receiptDoc",
      headerName: "Receipt",
      width: 150,
      sortable: false,
      renderCell: (params: any) => (
        <DocLink docKey={params.row?.receiptDocumentKey} label="Download" />
      ),
    },
    {
      field: "createdAtLabel",
      headerName: "Created At",
      width: 200,
      sortable: false,
    },
  ];

  return (
    <DashboardWrapper title="Payment" showBtn={false}>
      <>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={(e: string) => setSearchText(`${e}`)}
            customStyles={{ wrapper: { flex: 1, minWidth: "240px" } }}
          />
          <EntityFilter
            source="payment"
            filters={{ status: activeTab, searchText: searchText || undefined }}
            value={entityIds}
            onChange={setEntityIds}
            enabled={!isFetching}
          />
        </Box>
        <HorizontalTabs
          tabHeaders={paymentTabs}
          tabIndex={paymentTabs.findIndex((t) => t.value === activeTab)}
          onChange={(e: any) => {
            setActiveTab(e);
            navigate(`${path.payment}${e ? `?currentTab=${e}` : ""}`);
          }}
          components={paymentTabs.map(() => (
            <></>
          ))}
        />
        {isFetching ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "40px",
              background: "white",
            }}
          >
            <CircularProgress />
          </Box>
        ) : paymentData.rows.length > 0 ? (
          <DataGrid
            rows={paymentData.rows}
            columns={columns}
            rowCount={paymentData.rows.length}
            rowsPerPageOptions={[50]}
            pageSize={50}
            paginationMode="client"
            loading={isFetching}
            getRowId={(row) => row.id}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            buttonText=""
            showBtn={false}
            onClick={() => {}}
          />
        )}
      </>
    </DashboardWrapper>
  );
};

export default Payment;
